import type { RecordFilterOperator } from './types'

const PREFIX_TO_OPERATOR: Record<string, RecordFilterOperator> = {
  '=': 'eq',
  '!': 'neq',
  '>': 'gt',
  '<': 'lt',
}

// Parse a raw filter input into an operator + operand:
//   "=Active" → eq · "!Active" → neq · ">1990" → gt · "<M" → lt · "smith" → contains
// Returns null when the input is empty or is only a bare prefix (operand empty) — i.e. no filter.
export function parseFilter(
  raw: string | undefined,
): { operator: RecordFilterOperator; operand: string } | null {
  const s = (raw ?? '').trim()
  if (!s) return null
  const prefixed = PREFIX_TO_OPERATOR[s[0]]
  if (prefixed) {
    const operand = s.slice(1).trim()
    return operand ? { operator: prefixed, operand } : null
  }
  return { operator: 'contains', operand: s }
}

// Ordered comparison of two display strings, mirroring the demo's semantics: numeric when both parse
// as numbers, else chronological when both parse as dates, else case-insensitive lexical. Used only
// for client-side suggestion filtering of `>`/`<`; the data source enforces the authoritative order.
export function compareDisplay(a: string, b: string): number {
  const na = Number(a)
  const nb = Number(b)
  if (a.trim() !== '' && b.trim() !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
  const da = Date.parse(a)
  const db = Date.parse(b)
  if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db
  const la = a.toLowerCase()
  const lb = b.toLowerCase()
  return la < lb ? -1 : la > lb ? 1 : 0
}

// Whether a display value satisfies an operator/operand pair (client-side, for suggestion filtering).
export function displaySatisfies(
  display: string,
  operator: RecordFilterOperator,
  operand: string,
): boolean {
  const cell = display.toLowerCase()
  const q = operand.toLowerCase()
  switch (operator) {
    case 'contains':
      return cell.includes(q)
    case 'eq':
      return cell === q
    case 'neq':
      return cell !== q
    case 'gt':
      return compareDisplay(display, operand) > 0
    case 'lt':
      return compareDisplay(display, operand) < 0
  }
}
